import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import apiClient from '../../api';

function optionLabel(place) {
    const city = String(place?.city || '').trim();
    const state = String(place?.state || '').trim();
    if (city && state) return `${city}, ${state}`;
    return String(place?.label || '').trim();
}

/**
 * Free-text city/state field that suggests "City, ST" from Google Places as the user types.
 * A failed lookup still leaves the typed value in place.
 */
export default function CityStateAutocomplete({ label, value, onChange, error, helperText, required }) {
    const [input, setInput] = useState(value || '');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setInput(value || '');
    }, [value]);

    useEffect(() => {
        const q = String(input || '').trim();
        if (q.length < 2) {
            setOptions([]);
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setLoading(true);
            try {
                const res = await apiClient.get('/customers/googlemaps-search', {
                    params: { query: q, type: 'city' }
                });
                const rows = Array.isArray(res.data?.data) ? res.data.data : [];
                const labels = [];
                const seen = new Set();
                rows.forEach((row) => {
                    const next = optionLabel(row);
                    const key = next.toLowerCase();
                    if (!next || !row.city || !row.state || seen.has(key)) return;
                    seen.add(key);
                    labels.push(next);
                });
                if (!cancelled) setOptions(labels);
            } catch (err) {
                if (!cancelled) setOptions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [input]);

    const commit = (next) => {
        const text = next == null ? '' : String(next);
        setInput(text);
        onChange(text);
    };

    return (
        <Autocomplete
            freeSolo
            fullWidth
            options={options}
            loading={loading}
            value={value || null}
            inputValue={input}
            onInputChange={(event, next, reason) => {
                if (reason === 'input' || reason === 'clear') commit(next);
                else setInput(next || '');
            }}
            onChange={(event, next) => {
                commit(typeof next === 'string' ? next : '');
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    label={label}
                    required={required}
                    error={error}
                    helperText={helperText}
                />
            )}
        />
    );
}
